import { useState } from "react";
import { AlertTriangle, BadgeCheck } from "lucide-react";
import { api, safe } from "@/lib/api";
import { toast } from "sonner";
import { RATING } from "./KycCanvas";

export function KycRisk({ c }) {
  const r = c.risk;
  return (
    <div className="panel p-5" data-testid="kyc-risk">
      <div className="flex justify-between items-start">
        <div><div className="eyebrow">Customer risk rating</div><div className="text-[11px] text-slate-500 mt-0.5">Deterministic scoring model · {r.scale}</div></div>
        <div className="text-right">
          <span data-testid="kyc-risk-rating" className={`chip ring-1 border-0 text-sm ${RATING[r.rating]}`}>{r.rating}</span>
          <div className="num text-[11px] text-slate-500 mt-1" data-testid="kyc-risk-score">Score {r.score}</div>
        </div>
      </div>
      <table className="w-full text-xs mt-3"><tbody>
        {r.factors.map((f) => (
          <tr key={f.factor} className="border-t border-slate-100">
            <td className="py-1.5 text-slate-600">{f.factor}</td><td className="py-1.5 text-slate-900">{f.value}</td>
            <td className={`py-1.5 num text-right ${f.points ? "text-slate-900 font-semibold" : "text-slate-400"}`}>+{f.points}</td>
          </tr>
        ))}
      </tbody></table>
    </div>
  );
}

const DECISIONS = ["Approve", "Escalate to EDD", "Decline"];

export function KycSignoff({ c, onCase }) {
  const [decision, setDecision] = useState("Approve");
  const [rationale, setRationale] = useState("");
  const submit = safe(async () => {
    const { data } = await api.post(`/kyc/cases/${c.id}/signoff`, { decision, rationale });
    onCase(data);
    toast.success(`Case signed off: ${data.status}`);
  });
  if (c.signoff) {
    return (
      <div className="panel p-5 border-l-4 border-l-emerald-500" data-testid="kyc-signoff-record">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><BadgeCheck className="w-4 h-4 text-emerald-600" />Signed off: {c.signoff.decision}</div>
        <div className="text-xs text-slate-600 mt-1">{c.signoff.by} · {new Date(c.signoff.at).toLocaleString("en-US")} · risk {c.signoff.risk_rating} ({c.signoff.risk_score})</div>
        <div className="text-xs text-slate-800 mt-2 italic">{c.signoff.rationale}</div>
        <div className="text-[11px] text-slate-500 mt-2">The file is locked. Changes require a new periodic review.</div>
      </div>
    );
  }
  return (
    <div className="panel p-5 space-y-3" data-testid="kyc-signoff">
      <div className="flex justify-between"><div className="text-sm font-semibold text-slate-900">Analyst sign-off</div><div className="text-[11px] text-slate-500">Four-eyes: the signer must differ from whoever dispositioned matches</div></div>
      {c.blockers.length > 0 && (
        <div className="rounded-lg bg-amber-50 ring-1 ring-amber-200 p-3 space-y-1" data-testid="kyc-blockers">
          {c.blockers.map((b) => <div key={b} className="flex gap-2 text-[11px] text-amber-800"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{b}</div>)}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {DECISIONS.map((d) => (
          <button key={d} data-testid={`kyc-decision-${d.split(" ")[0].toLowerCase()}`} onClick={() => setDecision(d)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${decision === d ? "bg-sky-600 text-white border-sky-600" : "border-slate-200 text-slate-600 hover:border-sky-300"}`}>{d}</button>
        ))}
      </div>
      <textarea data-testid="kyc-signoff-rationale" value={rationale} onChange={(e) => setRationale(e.target.value)} rows={3} placeholder="Rationale for the decision (min 10 characters)"
        className="w-full text-xs px-3 py-2 rounded-lg border border-slate-200 outline-none focus:border-sky-400" />
      <button data-testid="kyc-signoff-submit" disabled={rationale.trim().length < 10 || (decision === "Approve" && c.blockers.length > 0)} onClick={submit} className="btn-primary disabled:opacity-40">Record decision</button>
    </div>
  );
}
